import { useEffect, useState } from "react";
import MultiSelectDropdown from "../../../shared/components/MultiSelectDropdown";
import SingleSelectDropdown from "../../../shared/components/SingleSelectDropdown";
import { getAdminDocumentTypes, getDocumentFamilies, getEntitiesByFamilyId, getUserCustomSettings } from "../services/adminDocuments_service";
import useAuthStore from "../../../store/authStore";
import { ADMIN_DOCUMENTS_ENDPOINTS } from "../../../app/apiEndpoints";
import axios from "axios";

const formatDateInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getInitialDates = () => {
  const today = new Date();

  // last day of month 4 months ago
  const fromDate = new Date(
    today.getFullYear(),
    today.getMonth() - 3,
    0
  );

  return {
    fromDate: formatDateInput(fromDate),
    toDate: formatDateInput(today)
  };
};

const DocumentsFilterPanel = ({ onApplyFilter, inputFilters, applyGridFilter, reapplyFilters, setReapplyFilters }) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const statusdata = [
    { text: "Published", value: "1" },
    { text: "UnPublished", value: "2" },
    { text: "Deleted", value: "3" }
  ];
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [documentFamilies, setDocumentFamilies] = useState([]);
  const [selectedDocumentFamily, setSelectedDocumentFamily] = useState(null);
  const [entities, setEntities] = useState([]);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const initialDates = getInitialDates();
  const user = useAuthStore((state)=> state.user);

  const [filters, setFilters] = useState({
    category: "",
    documentName: "",
    fromDate: initialDates.fromDate,
    toDate: initialDates.toDate,
    status: "",
    uploadedDate: ""
  });

  const fetchCategories = async () => {
    try{
      const availableCategoryResponse = await getUserCustomSettings(user, 'AdminDocument', 'Category');

      const categoryValue = availableCategoryResponse.data[0].value;
      const categoryValueList = categoryValue.split(',').map(Number);

      const response = await getAdminDocumentTypes();

      setCategories(response.data);

      const defaultCategories = response.data.filter((item) =>
        categoryValueList.includes(item.documentTypeID)
      );

      setSelectedCategories(defaultCategories);
    }
    catch{
      setCategories([]);
      setSelectedCategories([]);
    }
  };

  const fetchDocumentFamilies = async () => {
    try{
      const response = await getDocumentFamilies();
      setDocumentFamilies(response.data);
    }
    catch{
      setDocumentFamilies([]);
    }
  };

  const fetchFamilyEntities = async () => {
    try{
      const response = await getEntitiesByFamilyId(selectedDocumentFamily.familyID);
      setEntities(response.data);
      setSelectedEntities(response.data);
    }
    catch{
      setEntities([]);
      setSelectedEntities([]);
    }
  };

  useEffect(()=>{
    fetchCategories();
    fetchDocumentFamilies();
  },[]);

  useEffect(()=>{
    if(inputFilters === null){
      setFilters({
        category: "",
        documentName: "",
        fromDate: initialDates.fromDate,
        toDate: initialDates.toDate,
        status: "",
        uploadedDate: ""
      });
      setSelectedDocumentFamily(null);
      setSelectedEntities([]);
      setEntities([]);
      setSelectedStatus([]);
    }
  },[inputFilters]);

  useEffect(()=>{
    if(selectedDocumentFamily?.familyID !== 0){
      fetchFamilyEntities();
    }
  },[selectedDocumentFamily]);

  const handleChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  useEffect(()=>{
    if(filters){
      applyFilter(false);
    }
  },[filters]);

  useEffect(() => {
    if (reapplyFilters) {
      fetchCategories();
      applyFilter(false);
      setReapplyFilters(false);
    }
  }, [reapplyFilters]);

  const applyFilter = (applyOnGrid) => {
    const category = selectedCategories
      .map(c => c.documentTypeID)
      .join(",");

    const status = selectedStatus
      .map(s => s.value)
      .join(",");

    const contactids = selectedEntities
      .filter(e => e.entityTypeId === 0)
      .map(e => e.entityID)
      .join(",");

    const accountids = selectedEntities
      .filter(e => e.entityTypeId === 1)
      .map(e => e.entityID)
      .join(",");

    const processedFilters = {
      ...filters,
      category,
      status,
      contactids,
      accountids
    };

    delete processedFilters.entity;

    onApplyFilter(processedFilters);
    if(applyOnGrid){
      applyGridFilter(processedFilters);
    }
  };

  return (
    <div style={{ marginBottom: "20px" }}>
      {/* ROW 1 */}
      <div
        style={{
          display: "flex",
          flexDirection: 'row',
          gap: "15px",
          flexWrap: "wrap",
          marginBottom: "10px"
        }}
      >
        <div className="col" style={{width:'15%'}}>
          <label>Category</label>
          <MultiSelectDropdown
            options={categories}
            selectedValues={selectedCategories}
            onChange={(values) =>
              setSelectedCategories(values)
            }
            textField="documentTypeName"
            valueField="documentTypeID"
          />
        </div>

        <div className="col" style={{width:'15%'}}>
          <label>Family</label>
          <SingleSelectDropdown
            options={documentFamilies}
            selectedValue={selectedDocumentFamily}
            onChange={(value) =>
              setSelectedDocumentFamily(value)
            }
            textField="familyName"
            valueField="familyID"
          />
        </div>

        <div className="col" style={{width:'15%'}}>
          <label>Entity</label>
          <MultiSelectDropdown
            options={entities}
            selectedValues={selectedEntities}
            onChange={(values) =>
              setSelectedEntities(values)
            }
            textField="entity"
            valueField="entityID"
          />
        </div>

        <div className="col" style={{width:'15%'}}>
          <label>From</label>
          <input
            type="date"
            name="fromDate"
            className="form-control"
            value={filters.fromDate}
            onChange={handleChange}
          />
        </div>

        <div className="col" style={{width:'15%'}}>
          <label>To</label>
          <input
            type="date"
            name="toDate"
            className="form-control"
            value={filters.toDate}
            onChange={handleChange}
          />
        </div>

        <div className="col" style={{width:'15%'}}>
          <label>Status</label>
          <MultiSelectDropdown
            options={statusdata}
            selectedValues={selectedStatus}
            onChange={(values) =>
              setSelectedStatus(values)
            }
            textField="text"
            valueField="value"
          />
        </div>
      </div>

      {/* ROW 2 */}
      <div
        style={{
          display: "flex",
          flexDirection: 'row',
          gap: "15px",
          flexWrap: "wrap"
        }}
      >
        <div className="col" style={{width:'15%'}}>
          <label>Document Name</label>
          <input
            name="documentName"
            className="form-control"
            value={filters.documentName}
            onChange={handleChange}
          />
        </div>

        <div className="col" style={{width:'15%'}}>
          <label>Uploaded Date</label>
          <input
            type="date"
            name="uploadedDate"
            className="form-control"
            onChange={handleChange}
          />
        </div>

        <div className="col" style={{width:'68%', alignContent:'end'}}>
          <div style={{display: 'flex', flexDirection:'row', justifyContent:'end'}}>
            <button
              className="btn btn-primary"
              onClick={()=>applyFilter(true)}
            >
              Go
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsFilterPanel;
